// Ultimate Card Game (UCG) - Bot AI card hints (data only)
//
// What each card IS for, in the bot's vocabulary: a few role tags per card.
// The hand scorer does not read them yet - card-specific scoring still lives
// in js/engine/ai/cardRules.js - they are the shared language for what comes
// next: the Strategist's gambits ("play on Hyper units", "air switch") and
// deck building will ask "which of my cards are antiAir / outranger /
// frontlineStructure", instead of each keeping its own list of card names
// (docs/AI_MASTERPLAN_2026-10.md, sections 6.2 and 6.5, Anhang A).
//
// Roles are judgement calls about use, on top of what js/data/aiDoctrines.js
// cardTags() already derives from the stats (air, antiAir, structure,
// longRange, splash, cheap, heavy, cloak). tests/ai_card_hints_test.mjs keeps
// the two honest: a role that the stats can decide ('air', 'antiAir') must
// agree with them, every active card has an entry, and no entry names a card
// that no longer exists. A card without an entry is treated as having no roles
// (getCardHint), so a newly enabled card breaks nothing - the test just asks
// for its entry, like the other card-authoring checklists.

export const CARD_ROLES = Object.freeze([
  'frontline',          // cheap line infantry that holds and trades
  'tank',               // soaks damage for the army behind it
  'fast',               // arrives well before the rest of a wave
  'antiAir',            // can and is worth using against air
  'air',                // flies
  'antiInfantry',       // strong specifically against cheap infantry
  'armorBreaker',       // Energy damage or a bonus against deflection
  'splash',             // area damage, answers swarms
  'swarm',              // brings many bodies at once
  'kamikaze',           // one burst, then gone
  'mine',               // lies in wait at the frontline
  'detector',           // reveals cloaked units
  'cloak',              // is cloaked itself
  'outranger',          // out-ranges the base turret or most of the field
  'siege',              // pressures structures and the base from distance
  'frontlineStructure', // deployed at the territory divider
  'module',             // permanent base extension, never on the field
  'economy',            // makes plasma
  'steal',              // takes value from the opponent (units, bounties, hand)
  'buff',               // strengthens friendly units
  'reflect',            // returns damage to the attacker
  'slow',               // slows or roots targets
  'heavy',              // an expensive centrepiece
  'cycle',              // refreshes the hand
  'boardReset',         // rewrites the whole board
  'allIn',              // trades the army for a burst
  'finisher',           // wins games that are nearly won
  'removal',            // deletes a chosen target
  'wildcard',           // stands in for another card in a fusion
  'fusionFodder'        // three of a kind fuse into a stronger card
]);

export const CARD_HINTS = Object.freeze({
  // --- Human ---
  'Marine': { roles: ['frontline', 'antiAir', 'fusionFodder'] },
  'Tesla Walker': { roles: ['tank', 'armorBreaker', 'fusionFodder'] },
  'Devil Patrol': { roles: ['frontline', 'antiInfantry', 'armorBreaker', 'fusionFodder'] },
  'Hot Hand': { roles: ['frontline', 'fast'] },
  'Pink Beret': { roles: ['frontline', 'antiAir'] },
  'Hacker': { roles: ['slow', 'steal'] },
  'Wheelie Bomber': { roles: ['kamikaze', 'splash', 'armorBreaker', 'fast'] },
  'Panzer': { roles: ['tank', 'splash', 'fusionFodder'] },
  'Rocketship': { roles: ['air', 'antiAir'] },
  'Deployable Cannon': { roles: ['frontlineStructure', 'siege', 'tank'] },
  'Shade': { roles: ['cloak', 'outranger', 'fusionFodder'] },
  'A9 Piranha': { roles: ['air', 'antiAir'] },
  'A9 Grizzly': { roles: ['tank', 'detector', 'antiAir'] },
  'A9 Pigeon': { roles: ['air', 'antiAir', 'outranger', 'splash'] },
  'Fodder': { roles: ['swarm'] },
  'Reconsider': { roles: ['cycle'] },
  'Mental Extraction': { roles: ['steal'] },
  'Invasion Capsule Sling': { roles: ['module', 'splash'] },
  'Drone Command': { roles: ['module', 'splash'] },
  'Congress Module': { roles: ['module', 'buff', 'economy'] },
  'The Salvage Company': { roles: ['economy'] },
  'Orchestra': { roles: ['outranger', 'siege', 'splash'] },
  'Stickman': { roles: ['outranger', 'tank', 'splash', 'antiAir'] },

  // --- Sul ---
  'Defective Loaf': { roles: ['frontline', 'fusionFodder'] },
  'Rejected Loaf': { roles: ['tank', 'reflect', 'fusionFodder'] },
  'Apostate Loaf': { roles: ['slow', 'fusionFodder'] },
  'Forgotten Loaf': { roles: ['kamikaze', 'mine', 'splash', 'antiAir'] },
  'Delving Pillar': { roles: ['air', 'detector'] },
  'Harvesting Pillar': { roles: ['slow', 'antiAir'] },
  'Processing Pillar': { roles: ['splash', 'antiAir', 'armorBreaker'] },
  'Claiming Pillar': { roles: ['economy', 'frontlineStructure'] },
  'Preserver Lock': { roles: ['armorBreaker'] },
  'Sentinal Lock': { roles: ['air', 'antiAir'] },
  'Bastion Lock': { roles: ['tank', 'splash'] },
  'Monitor Lock': { roles: ['frontlineStructure', 'buff'] },
  'Enforcer Vulture': { roles: ['air', 'antiAir', 'heavy'] },
  'Looting Vulture': { roles: ['air', 'antiAir', 'heavy', 'steal', 'economy', 'armorBreaker'] },
  'Parasite Vulture': { roles: ['air', 'heavy', 'steal'] },
  'Anti Shade': { roles: ['cloak', 'frontline'] },
  'Ambush': { roles: ['frontline', 'swarm', 'allIn'] },
  'Forced Overstimulation': { roles: ['allIn', 'finisher'] },
  'Experimental Metamorphosis': { roles: ['boardReset'] },
  'Precision Strike': { roles: ['removal', 'armorBreaker'] },
  'Retreat Protocol': { roles: ['boardReset', 'tank', 'economy'] },
  'Pulp': { roles: ['wildcard', 'splash'] },

  // --- Fusion results (never in a deck, they appear when three cards fuse) ---
  'Hyper Marine': { roles: ['frontline', 'antiAir'] },
  'Hyper Shade': { roles: ['cloak', 'outranger'] },
  'Hyper Devil Patrol': { roles: ['air', 'antiInfantry', 'armorBreaker'] },
  'Hyper Tesla Walker': { roles: ['tank', 'armorBreaker', 'antiInfantry'] },
  'Hyper Panzer': { roles: ['tank', 'splash', 'antiAir', 'heavy'] }
});

const NO_HINT = Object.freeze({ roles: Object.freeze([]) });

export function getCardHint(cardOrName) {
  const name = typeof cardOrName === 'string' ? cardOrName : cardOrName?.name;
  return CARD_HINTS[name] || NO_HINT;
}

export function cardHasRole(cardOrName, role) {
  return getCardHint(cardOrName).roles.includes(role);
}
